import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { ClearRounded, Search, ThreeDRotation } from '@mui/icons-material';
import {
	Checkbox,
	Divider,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	SxProps,
	Stack,
	StackProps,
	TextField,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';

import { usePersistedSetting } from '@@common';

import { ViewLoader } from './ViewLoader';

type ListWithFilterProps<T> = {
	items: T[];
	isLoading?: boolean;
	loadingIndicator?: React.ReactNode;
	selectedItemId?: string;
	onSelected: (item: T) => void;
	placeholder: string;
	showDescriptionToggle?: boolean;
	getKey: (item: T) => string;
	getDisplayName: (item: T) => string;
	getDescription?: (item: T) => string | undefined;
	getGroup?: (item: T) => string | undefined;
	maxHeight?: string;
	emptyMessage?: string;
	icon?: (item: T) => React.ReactNode;
	nameSuffix?: (item: T) => React.ReactNode;
	trailingContent?: (item: T) => React.ReactNode;
	itemSx?: (item: T) => SxProps<Theme> | undefined;
	filterActions?: React.ReactNode;
	/** localStorage key prefix for persisting settings (e.g. show descriptions). When omitted, a global key is used. */
	settingsKey?: string;
} & React.PropsWithChildren<StackProps>;

export default function ListWithFilter<T>({
	items,
	isLoading,
	loadingIndicator,
	selectedItemId,
	onSelected,
	showDescriptionToggle,
	getKey,
	getDisplayName,
	getDescription,
	getGroup,
	placeholder,
	maxHeight,
	emptyMessage,
	icon,
	nameSuffix,
	trailingContent,
	itemSx,
	filterActions,
	settingsKey,
	...props
}: ListWithFilterProps<T>) {
	const showDescriptionKey = settingsKey ? `${settingsKey}.showDescription` : 'listWithFilter.showDescription';
	const [nameFilter, setNameFilter] = useState('');
	const listRef = useRef<HTMLUListElement>(null);
	const showDescriptionControlVisible = Boolean(showDescriptionToggle && getDescription);

	useEffect(() => {
		if (!selectedItemId || !listRef.current) return;
		const el = listRef.current.querySelector<HTMLElement>(`[data-item-key="${CSS.escape(selectedItemId)}"]`);
		el?.scrollIntoView({ block: 'nearest' });
	}, [selectedItemId, items]);
	const [showDescription, setShowDescription] = usePersistedSetting(showDescriptionKey, false);

	const filteredExpressionMetadataList = React.useMemo(() => {
		const lowerFilter = nameFilter.toLowerCase();
		return items.filter((x) => {
			if (getDisplayName(x).toLowerCase().includes(lowerFilter)) return true;
			if (getGroup?.(x)?.toLowerCase().includes(lowerFilter)) return true;
			return false;
		});
	}, [nameFilter, items, getDisplayName, getGroup]);

	const groupedItems = React.useMemo(() => {
		if (!getGroup) {
			return { '': filteredExpressionMetadataList };
		}

		return filteredExpressionMetadataList.reduce(
			(groups, item) => {
				const groupKey = getGroup(item) || 'Other';
				if (!groups[groupKey]) {
					groups[groupKey] = [];
				}
				groups[groupKey].push(item);
				return groups;
			},
			{} as Record<string, T[]>,
		);
	}, [filteredExpressionMetadataList, getGroup]);

	const handleNameFilterChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setNameFilter(e.target.value);
	}, []);

	const handleClearFilter = React.useCallback(() => {
		setNameFilter('');
	}, []);

	const handleShowDescriptionChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setShowDescription(e.target.checked);
		},
		[setShowDescription],
	);

	const renderItem = (x: T) => {
		const itemKey = getKey(x);
		const displayName = getDisplayName(x);
		const description = (showDescriptionToggle ? showDescription : true) ? getDescription?.(x) : undefined;
		const primaryContent = nameSuffix ? (
			<span>
				{displayName}
				{nameSuffix(x)}
			</span>
		) : (
			displayName
		);
		return (
			<ListItemButton key={itemKey} selected={selectedItemId === itemKey} title={description} data-item-key={itemKey} sx={itemSx?.(x)}>
				<ListItemIcon>{icon ? icon(x) : <ThreeDRotation />}</ListItemIcon>
				<ListItemText primary={primaryContent} secondary={description} />
				{trailingContent?.(x)}
			</ListItemButton>
		);
	};

	const renderListItems = () => {
		if (!getGroup) {
			// Render without groups
			return filteredExpressionMetadataList.map((x) => (
				<ListItem key={getKey(x)} disablePadding onClick={() => onSelected(x)}>
					{renderItem(x)}
				</ListItem>
			));
		}

		// Render with groups
		const groupKeys = Object.keys(groupedItems).sort();
		return groupKeys.map((groupKey) => (
			<React.Fragment key={groupKey}>
				{groupKey !== '' && (
					<ListItem>
						<Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', py: 1 }}>
							{groupKey}
						</Typography>
					</ListItem>
				)}
				{groupedItems[groupKey].map((x) => (
					<ListItem key={getKey(x)} disablePadding onClick={() => onSelected(x)} sx={{ pl: groupKey !== '' ? 2 : 0 }}>
						{renderItem(x)}
					</ListItem>
				))}
				{groupKey !== '' && groupKeys.indexOf(groupKey) < groupKeys.length - 1 && <Divider />}
			</React.Fragment>
		));
	};

	return (
		<Stack direction="column" sx={{ flex: 1 }} spacing={1} {...props}>
			<Stack direction="row" spacing={1} alignItems="center">
				<FilterTextField
					placeholder={placeholder}
					nameFilter={nameFilter}
					showDescription={showDescription}
					showDescriptionControlVisible={showDescriptionControlVisible}
					onNameFilterChange={handleNameFilterChange}
					onClearFilter={handleClearFilter}
					onShowDescriptionChange={handleShowDescriptionChange}
				/>
				{filterActions}
			</Stack>
			<List ref={listRef} sx={{ overflowY: 'auto', flexGrow: 1, flexShrink: 1, maxHeight }}>
				{renderListItems()}
				{!isLoading && filteredExpressionMetadataList.length === 0 && <ListItem alignItems="center">{emptyMessage ?? 'No items'}</ListItem>}
				{isLoading && (loadingIndicator ?? <ViewLoader />)}
			</List>
		</Stack>
	);
}

type FilterTextFieldProps = {
	placeholder: string;
	nameFilter: string;
	showDescription: boolean;
	showDescriptionControlVisible: boolean;
	onNameFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onClearFilter: () => void;
	onShowDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function FilterTextField({
	placeholder,
	nameFilter,
	showDescription,
	showDescriptionControlVisible,
	onNameFilterChange,
	onClearFilter,
	onShowDescriptionChange,
}: FilterTextFieldProps) {
	const startAdornment = React.useMemo(
		() => (
			<InputAdornment position="start">
				<Search fontSize="small" />
			</InputAdornment>
		),
		[],
	);

	const endAdornment = React.useMemo(() => {
		if (!nameFilter && !showDescriptionControlVisible) {
			return undefined;
		}

		return (
			<InputAdornment position="end">
				{nameFilter && (
					<Tooltip title="Clear">
						<IconButton size="small" tabIndex={-1} onClick={onClearFilter} sx={{ p: 0.5 }}>
							<ClearRounded fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
				{showDescriptionControlVisible && (
					<Tooltip title="Show Descriptions">
						<Checkbox checked={showDescription} onChange={onShowDescriptionChange} size="small" tabIndex={-1} sx={{ p: 0.5 }} />
					</Tooltip>
				)}
			</InputAdornment>
		);
	}, [nameFilter, onClearFilter, onShowDescriptionChange, showDescription, showDescriptionControlVisible]);

	const inputSlotProps = React.useMemo(
		() => ({
			startAdornment,
			endAdornment,
		}),
		[endAdornment, startAdornment],
	);

	const textFieldSlotProps = React.useMemo(
		() => ({
			input: inputSlotProps,
		}),
		[inputSlotProps],
	);

	return <TextField size="small" fullWidth placeholder={placeholder} value={nameFilter} onChange={onNameFilterChange} slotProps={textFieldSlotProps} />;
}
